import { useEffect, useRef } from 'react';
import { create } from 'zustand';

type RemoveTopicModalStore = {
  isOpen: boolean;
  topicId: string;
  topicName: string;
  topicGroupId: string;
  topicGroupName: string;
  openModal: (params: { topicId: string; topicName?: string; topicGroupId: string; topicGroupName?: string }) => void;
  closeModal: () => void;
  clearModal: () => void;
};

/**
 * Zustand store for the remove topic modal actions when removing a topic from a topic group.
 */
const useRemoveTopicModalStore = create<RemoveTopicModalStore>((set) => ({
  isOpen: false,
  topicId: '',
  topicName: '',
  topicGroupId: '',
  topicGroupName: '',
  openModal: ({ topicId, topicName, topicGroupId, topicGroupName }) =>
    set({ isOpen: true, topicId, topicName, topicGroupId, topicGroupName }),
  closeModal: () => set({ isOpen: false }),
  clearModal: () => set({ topicId: '', topicName: '', topicGroupId: '', topicGroupName: '' })
}));

export const useRemoveTopicModal = () => {
  const { isOpen, topicId, topicName, topicGroupId, openModal, closeModal, clearModal, topicGroupName } =
    useRemoveTopicModalStore();
  const timer = useRef<NodeJS.Timeout | null>(null);

  // Clear the modal after it has closed to avoid flashing empty modal content
  useEffect(() => {
    if (!isOpen) {
      timer.current = setTimeout(() => {
        clearModal();
      }, 100);
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [isOpen, clearModal]);

  return {
    isOpen,
    topicId,
    topicName,
    topicGroupId,
    topicGroupName,
    openModal,
    closeModal
  };
};
